import { allocate, roundHalfUp, type Minor } from './money'
import { toUnits } from '@/stores/cartStore'

/**
 * The client half of the cart calculation.
 *
 * It mirrors `App\Services\Sales\CartCalculator` step for step, and the
 * agreement between them is asserted against a shared fixture generated from
 * the server itself (see cart.test.ts). This exists for INSTANT FEEDBACK
 * only — the server recomputes everything from its own catalogue, and a
 * mismatch beyond one minor unit rejects the sale with a 409 rather than
 * writing a figure nobody can defend.
 *
 * Two rules from the server carry over exactly, and both are the difference
 * between a receipt that adds up and one that does not:
 *
 *   - Tax on an INCLUSIVE price is EXTRACTED, and the tax is derived as
 *     (total − subtotal) rather than computed independently. Computing it
 *     directly lets the two disagree by a paisa on some values.
 *   - The order discount is ALLOCATED across lines so the shares sum exactly
 *     to the discount, never computed per line and rounded.
 *
 * There is no float in this file, and no parseFloat or toFixed — both are
 * ESLint-banned inside features/pos for the same reason.
 */

export type CalcLine = {
  key: string
  quantity: string
  unitPrice: number
  discountType: 'percentage' | 'amount' | null
  discountValue: string
  /** Percent as a decimal string, e.g. "18" or "2.5". Null means untaxed. */
  taxRate: string | null
  priceIncludesTax: boolean
}

export type CalculatedLine = {
  key: string
  gross: number
  lineDiscount: number
  allocatedOrderDiscount: number
  subtotal: number
  tax: number
  total: number
}

export type CartTotals = {
  lines: CalculatedLine[]
  subtotal: number
  lineDiscountTotal: number
  orderDiscount: number
  tax: number
  rounding: number
  total: number
  taxBreakdown: Array<{ rate: string; taxable: number; tax: number }>
}

const QUANTITY_SCALE = 10_000

/** price x quantity, in ten-thousandths, rounded half-up to a whole minor unit. */
function priceFor(quantity: string, unitPrice: number): number {
  return roundHalfUp((toUnits(quantity) * unitPrice) / QUANTITY_SCALE)
}

function percentageOf(amount: number, rate: string): number {
  const parsed = Number(rate)

  if (!Number.isFinite(parsed) || parsed === 0) return 0

  return roundHalfUp((amount * parsed) / 100)
}

function discountOn(
  amount: number,
  type: 'percentage' | 'amount' | null,
  value: string,
): number {
  if (type === null || value === '') return 0

  if (type === 'percentage') {
    return Math.min(percentageOf(amount, value), amount)
  }

  // An amount discount arrives in MAJOR units from the input, already
  // converted to minor by the caller.
  const parsed = Number(value)

  return Number.isFinite(parsed) ? Math.min(Math.max(0, Math.trunc(parsed)), amount) : 0
}

/**
 * The subtotal hiding inside a tax-inclusive amount.
 *
 * Exported for the till on the signup page, which shows a GST split to people
 * who do not have an account yet. It uses THIS function rather than its own
 * copy: two implementations of the same tax rule drift, and the one on the
 * marketing surface is the one nobody would notice drifting.
 *
 * Callers take the tax as `inclusive - subtotalWithin(...)` rather than
 * rounding it separately — that is what makes the parts sum back to the whole.
 */
export function subtotalWithin(inclusive: number, rate: string): number {
  const parsed = Number(rate)

  if (!Number.isFinite(parsed) || parsed === 0) return inclusive

  return roundHalfUp((inclusive * 100) / (100 + parsed))
}

/**
 * Cash rounding, as a visible adjustment.
 *
 * Returns the DELTA, not the rounded total, so the receipt can show it on its
 * own line and the total still equals the sum of its parts.
 */
export function cashAdjustment(total: number, nearest: number): number {
  if (nearest <= 1) return 0

  const remainder = total % nearest

  return remainder === 0
    ? 0
    : remainder * 2 >= nearest
      ? nearest - remainder
      : -remainder
}

export function calculateCart(
  lines: CalcLine[],
  {
    orderDiscountType = null,
    orderDiscountValue = '',
    cashRounding = 0,
    applyCashRounding = false,
  }: {
    orderDiscountType?: 'percentage' | 'amount' | null
    orderDiscountValue?: string
    cashRounding?: number
    applyCashRounding?: boolean
  } = {},
): CartTotals {
  // --- 1 & 2: line subtotals, less line discounts -------------------------
  const staged = lines.map((line) => {
    const gross = priceFor(line.quantity, line.unitPrice)
    const lineDiscount = discountOn(gross, line.discountType, line.discountValue)

    return {
      line,
      gross,
      lineDiscount,
      // Clamped: a 120% line discount is a data-entry error, and a negative
      // line total would poison every sum downstream.
      afterLineDiscount: Math.max(0, gross - lineDiscount),
      allocatedOrderDiscount: 0,
      subtotal: 0,
      tax: 0,
      total: 0,
    }
  })

  // --- 3: allocate the order discount -------------------------------------
  const orderDiscountable = staged.reduce((sum, item) => sum + item.afterLineDiscount, 0)
  const orderDiscount = Math.min(
    discountOn(orderDiscountable, orderDiscountType, orderDiscountValue),
    orderDiscountable,
  )

  if (orderDiscount > 0 && staged.length > 0) {
    const shares = allocate(
      orderDiscount as Minor,
      staged.map((item) => item.afterLineDiscount),
    )

    staged.forEach((item, index) => {
      item.allocatedOrderDiscount = shares[index]
    })
  }

  // --- 4: tax, per line, on the post-discount amount ----------------------
  for (const item of staged) {
    const net = Math.max(0, item.afterLineDiscount - item.allocatedOrderDiscount)
    const rate = item.line.taxRate

    if (rate === null || Number(rate) === 0) {
      item.subtotal = net
      item.tax = 0
      item.total = net
      continue
    }

    if (item.line.priceIncludesTax) {
      item.total = net
      item.subtotal = subtotalWithin(net, rate)
      // Derived, never computed independently — this subtraction is what
      // guarantees subtotal + tax === total exactly.
      item.tax = item.total - item.subtotal
    } else {
      item.subtotal = net
      item.tax = percentageOf(net, rate)
      item.total = item.subtotal + item.tax
    }
  }

  // --- 5: order totals ----------------------------------------------------
  const subtotal = staged.reduce((sum, item) => sum + item.subtotal, 0)
  const tax = staged.reduce((sum, item) => sum + item.tax, 0)
  const lineDiscountTotal = staged.reduce((sum, item) => sum + item.lineDiscount, 0)

  let total = subtotal + tax

  // --- 6: cash rounding, only when the tender is cash ---------------------
  let rounding = 0

  if (applyCashRounding && cashRounding > 1) {
    rounding = cashAdjustment(total, cashRounding)
    total += rounding
  }

  return {
    lines: staged.map((item) => ({
      key: item.line.key,
      gross: item.gross,
      lineDiscount: item.lineDiscount,
      allocatedOrderDiscount: item.allocatedOrderDiscount,
      subtotal: item.subtotal,
      tax: item.tax,
      total: item.total,
    })),
    subtotal,
    lineDiscountTotal,
    orderDiscount,
    tax,
    rounding,
    total,
    taxBreakdown: buildTaxBreakdown(staged),
  }
}

function buildTaxBreakdown(
  staged: Array<{ line: CalcLine; subtotal: number; tax: number }>,
): Array<{ rate: string; taxable: number; tax: number }> {
  const bands = new Map<string, { rate: string; taxable: number; tax: number }>()

  for (const item of staged) {
    const rate = item.line.taxRate

    if (rate === null || Number(rate) === 0) continue

    const band = bands.get(rate) ?? { rate, taxable: 0, tax: 0 }

    band.taxable += item.subtotal
    band.tax += item.tax
    bands.set(rate, band)
  }

  return [...bands.values()].sort((a, b) => Number(a.rate) - Number(b.rate))
}
